import React, { useState, useRef } from 'react';
import { useApp } from '../../../context/AppContext';
import FormGroup from '../../common/FormGroup/FormGroup';
import Button from '../../common/Button/Button';
import { questions, getLevel, part6Passage } from './questions';
import { fetchCsrfToken, submitBooking } from '../../../services/api';
import styles from './ToeicPlacementTest.module.css';

const STEPS = {
  INFO: 'info',
  QUIZ: 'quiz',
  RESULT: 'result',
};

/**
 * TOEIC Placement Test — 3-step quiz: Info → Questions → Result
 */
function ToeicPlacementTest() {
  const { openModal } = useApp();
  const audioRef = useRef(null);
  const [step, setStep] = useState(STEPS.INFO);
  const [userInfo, setUserInfo] = useState({
    name: '',
    phone: '',
    email: '',
    mode: 'Online (Zoom)',
  });
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ═══ Step 1: Info Collection ═══
  function handleInfoChange(field) {
    return (e) => {
      setError(null);
      setUserInfo((prev) => ({ ...prev, [field]: e.target.value }));
    };
  }

  function startQuiz() {
    if (!userInfo.name.trim() || !userInfo.phone.trim()) {
      setError('Vui lòng điền họ tên và số điện thoại để tiếp tục.');
      return;
    }
    setStep(STEPS.QUIZ);
  }

  // ═══ Step 2: Quiz ═══
  function selectOption(index) {
    if (showFeedback) return; // Already answered
    setSelectedOption(index);
    setShowFeedback(true);
    const isCorrect = index === questions[currentQuestion].correctAnswer;
    setAnswers((prev) => [...prev, isCorrect ? 1 : 0]);
  }

  function nextQuestion() {
    if (!showFeedback) return;

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
      setSelectedOption(null);
      setShowFeedback(false);
    } else {
      showResult();
    }
  }

  // ═══ Step 3: Result ═══
  async function showResult() {
    const score = answers.reduce((sum, val) => sum + val, 0);
    const levelData = getLevel(score);

    // Submit to backend/Google Sheets
    setLoading(true);
    try {
      await submitQuizResult({
        ...userInfo,
        score,
        level: levelData.level,
        toeicRange: levelData.toeicRange,
      });
    } catch (err) {
      console.error('Failed to submit quiz result:', err);
    } finally {
      setLoading(false);
    }

    setStep(STEPS.RESULT);
  }

  async function submitQuizResult(data) {
    const token = await fetchCsrfToken();
    await submitBooking({
      name: data.name,
      phone: data.phone,
      email: data.email || '',
      course: `TOEIC Test - Score: ${data.score}/15 - ${data.level}`,
      mode: data.mode,
    }, token);
  }

  function restartQuiz() {
    setStep(STEPS.INFO);
    setCurrentQuestion(0);
    setAnswers([]);
    setSelectedOption(null);
    setShowFeedback(false);
    setUserInfo({ name: '', phone: '', email: '', mode: 'Online (Zoom)' });
  }

  function confirmRegistration() {
    openModal();
  }

  // ═══ Render ═══
  const score = answers.reduce((sum, val) => sum + val, 0);
  const levelData = getLevel(score);
  const progress = ((currentQuestion / questions.length) * 100).toFixed(0);
  const currentQ = questions[currentQuestion];

  return (
    <section className="section bg-cream" id="toeic-test" aria-label="TOEIC Placement Test">
      <div className="container">
        {/* Section Header */}
        <div className="section-header center" style={{ marginBottom: '40px' }}>
          <div className="section-label">Kiểm tra đầu vào</div>
          <h2 className="section-title">Bài test TOEIC đầu vào — miễn phí</h2>
          <p className="section-desc">
            Hoàn thành 15 câu hỏi (Part 1, 5 & 6) để Annie đánh giá trình độ hiện tại và tư vấn lộ trình phù hợp
            nhất với bạn.
          </p>
        </div>

        <div className={styles.outer}>
          {/* ────── STEP 1: Info ────── */}
          {step === STEPS.INFO && (
            <div className={styles.card}>
              <div className={styles.stepLabel}>Bước 1 / 3</div>
              <h3 className={styles.title}>Thông tin của bạn</h3>
              <p className={styles.subtitle}>
                Để Annie gửi kết quả và tư vấn lộ trình cá nhân hoá.
              </p>
              <FormGroup
                label="Họ và tên *"
                placeholder="Nguyễn Văn A"
                value={userInfo.name}
                onChange={handleInfoChange('name')}
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <FormGroup
                  label="Số điện thoại / Zalo *"
                  type="tel"
                  placeholder="0909 xxx xxx"
                  value={userInfo.phone}
                  onChange={handleInfoChange('phone')}
                />
                <FormGroup
                  label="Email"
                  type="email"
                  placeholder="email@gmail.com"
                  value={userInfo.email}
                  onChange={handleInfoChange('email')}
                />
              </div>
              <FormGroup
                label="Hình thức học mong muốn"
                type="select"
                value={userInfo.mode}
                onChange={handleInfoChange('mode')}
              >
                <option>Online (Zoom)</option>
                <option>Offline – Safira Khang Điền, Q9</option>
                <option>Linh hoạt cả hai</option>
              </FormGroup>
              {error && (
                <p role="alert" style={{ color: '#c0392b', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  {error}
                </p>
              )}
              <Button variant="primary" fullWidth onClick={startQuiz} style={{ marginTop: '8px' }}>
                Bắt đầu làm bài test →
              </Button>
            </div>
          )}

          {/* ────── STEP 2: Quiz ────── */}
          {step === STEPS.QUIZ && (
            <>
              <div className={styles.progressWrap}>
                <div className={styles.progressBar} style={{ width: `${progress}%` }} />
              </div>

              {/* Part 6 Passage Display */}
              {currentQ.isPart6 && (
                <div className={styles.passageCard}>
                  <div className={styles.passageHeader}>
                    <strong>{part6Passage.title}</strong>
                    <div className={styles.passageAuthor}>{part6Passage.author}</div>
                  </div>
                  <div className={styles.passageContent}>
                    {part6Passage.content.split('\n\n').map((para, idx) => (
                      <p key={idx} className={styles.passageParagraph}>
                        {para}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Part 1 Listening - Image & Audio Display */}
              {currentQ.isListening && (
                <div className={styles.listeningCard}>
                  <div className={styles.imageContainer}>
                    <img
                      src={currentQ.image}
                      alt={`TOEIC Part 1 Question ${currentQ.id}`}
                      className={styles.questionImage}
                    />
                  </div>
                  <div className={styles.audioContainer}>
                    <audio
                      ref={audioRef}
                      src={currentQ.audio}
                      controls
                      className={styles.audioPlayer}
                    >
                      Your browser does not support the audio element.
                    </audio>
                    <p className={styles.audioInstruction}>
                      🎧 Nghe audio và chọn câu mô tả đúng nhất về bức ảnh
                    </p>
                  </div>
                </div>
              )}

              <div className={styles.card}>
                <div className={styles.stepLabel}>
                  Câu {currentQuestion + 1} / {questions.length}
                </div>
                <div className={styles.partTag}>{currentQ.part}</div>
                {currentQ.isPart6 && (
                  <p className={styles.instruction}>
                    Chọn câu trả lời phù hợp nhất cho chỗ trống <strong>[{currentQ.blankNumber}]</strong> trong đoạn văn trên.
                  </p>
                )}
                {!currentQ.isPart6 && <p className={styles.question}>{currentQ.question}</p>}
                <div className={styles.options}>
                  {currentQ.options.map((opt, idx) => {
                    const isCorrect = idx === currentQ.correctAnswer;
                    const isSelected = idx === selectedOption;
                    let optionClass = styles.option;
                    if (showFeedback) {
                      optionClass += ' ' + styles.disabled;
                      if (isCorrect) optionClass += ' ' + styles.correct;
                      else if (isSelected) optionClass += ' ' + styles.wrong;
                    } else if (isSelected) {
                      optionClass += ' ' + styles.selected;
                    }

                    return (
                      <div key={idx} className={optionClass} onClick={() => selectOption(idx)}>
                        <span className={styles.optionLetter}>{opt.charAt(0)}</span>
                        {opt.substring(3)}
                      </div>
                    );
                  })}
                </div>
                <Button
                  variant="primary"
                  fullWidth
                  onClick={nextQuestion}
                  disabled={!showFeedback}
                  loading={loading}
                  style={{ opacity: showFeedback && !loading ? 1 : 0.4, pointerEvents: showFeedback && !loading ? 'auto' : 'none' }}
                >
                  {loading ? 'Đang gửi...' : (currentQuestion < questions.length - 1 ? 'Câu tiếp theo →' : 'Xem kết quả →')}
                </Button>
              </div>
            </>
          )}

          {/* ────── STEP 3: Result ────── */}
          {step === STEPS.RESULT && (
            <div className={`${styles.card} ${styles.resultCard}`}>
              <div className={styles.resultIcon}>🎯</div>
              <h3 className={styles.title}>Kết quả của {userInfo.name || 'bạn'}</h3>
              <div className={styles.scoreWrap}>
                <div className={styles.score}>{score}</div>
                <div className={styles.scoreLabel}>/ 15 câu đúng</div>
              </div>
              <div className={`${styles.levelBadge} ${styles[levelData.color]}`}>
                {levelData.level} ({levelData.toeicRange})
              </div>
              <p className={styles.description}>{levelData.description}</p>
              <div className={styles.roadmap}>
                <strong>Lộ trình gợi ý:</strong> {levelData.roadmap}
              </div>
              <div className={styles.contactInfo}>
                <div className={styles.contactIcon}>📱</div>
                <div className={styles.contactText}>
                  <strong>Annie sẽ liên hệ trong 24h</strong>
                  để tư vấn lộ trình cá nhân hoá và chốt lịch học thử miễn phí.
                </div>
              </div>
              <div className={styles.buttonGroup}>
                <Button variant="primary" fullWidth onClick={confirmRegistration}>
                  Xác nhận đăng ký tư vấn 🌸
                </Button>
                <Button variant="outline" fullWidth onClick={restartQuiz}>
                  Làm lại bài test
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default ToeicPlacementTest;
